import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  MessageCircle,
  Phone,
  HelpCircle,
  Facebook,
  Youtube,
} from 'lucide-react';
import BreadcrumbNav from '../components/common/BreadcrumbNav';
import SEOHead from '../components/common/SEOHead';
import {
  BRAND_FULL_NAME,
  BRAND_NAME,
  BRAND_TEACHER_NAME,
  CONTACT_FACEBOOK_URL,
  CONTACT_WHATSAPP,
  CONTACT_WHATSAPP_URL,
  CONTACT_YOUTUBE_URL,
  SITE_URL,
} from '../config/brand';

const faqs = [
  {
    q: 'ما هي منصة نهج الأزهر؟',
    a: 'منصة تعليمية أونلاين لطلاب الثانوية الأزهرية تقدّم كورسات في اللغة العربية والعلم الشرعي مع الأستاذ سعيد الفقي.',
  },
  {
    q: 'كيف أسجّل في كورس؟',
    a: 'أنشئ حساباً مجاناً، اختر الصف والكورس المناسب، ثم أكمل الاشتراك عبر الدفع أو كود التفعيل.',
  },
  {
    q: 'هل يمكن لولي الأمر متابعة تقدّم الطالب؟',
    a: 'نعم، عبر صفحة متابعة ولي الأمر بإدخال رقم هاتف ولي الأمر ورقم هاتف الطالب المسجّل.',
  },
  {
    q: 'ما سياسة الاسترجاع؟',
    a: 'المنتجات رقمية؛ الاسترجاع محدود للحالات التقنية أو التحصيل الخاطئ وفق سياسة الاسترجاع المنشورة.',
  },
];

const ContactPage: React.FC = () => {
  const breadcrumbs = [{ name: 'تواصل معنا', path: '/contact' }];

  const contactJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: `تواصل معنا | ${BRAND_NAME}`,
    url: `${SITE_URL}/contact`,
    description: `تواصل مع فريق ${BRAND_FULL_NAME} — دعم طلاب الثانوية الأزهرية مع الأستاذ ${BRAND_TEACHER_NAME}.`,
    mainEntity: {
      '@type': 'EducationalOrganization',
      name: BRAND_FULL_NAME,
      url: SITE_URL,
      telephone: `+${CONTACT_WHATSAPP}`,
      email: 'info@nahgelazhar.com',
      sameAs: [CONTACT_FACEBOOK_URL, CONTACT_YOUTUBE_URL, CONTACT_WHATSAPP_URL],
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50/40 via-white to-gold-50/20" dir="rtl">
      <SEOHead
        title={`تواصل معنا | ${BRAND_NAME}`}
        description={`تواصل مع منصة نهج الأزهر — واتساب، فيسبوك ويوتيوب الأستاذ سعيد الفقي. دعم طلاب الثانوية الأزهرية وأولياء الأمور.`}
        keywords={`تواصل نهج الأزهر, واتساب سعيد الفقي, دعم طلاب الأزهر`}
        url="/contact"
        breadcrumbs={breadcrumbs}
        structuredData={[contactJsonLd, faqJsonLd]}
      />

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <BreadcrumbNav items={breadcrumbs} />

        <header className="mb-8 text-center sm:mb-10 sm:text-right">
          <h1 className="mb-3 text-2xl font-bold text-green-900 sm:text-3xl lg:text-4xl font-cairo-bold">
            تواصل معنا
          </h1>
          <p className="mx-auto max-w-2xl text-sm leading-relaxed text-gray-600 sm:mx-0 sm:text-base">
            فريق {BRAND_FULL_NAME} جاهز لمساعدتك في التسجيل، الاشتراك، ومتابعة التعلّم مع الأستاذ{' '}
            {BRAND_TEACHER_NAME}.
          </p>
        </header>

        <section className="mb-10 grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 lg:grid-cols-3">
          <a
            href={CONTACT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-[7rem] flex-col items-center justify-center gap-2 rounded-2xl border border-green-100 bg-white p-5 shadow-sm transition hover:border-green-300 hover:shadow-md"
          >
            <MessageCircle className="h-7 w-7 text-green-700" />
            <span className="font-semibold text-green-900">واتساب</span>
            <span className="text-xs text-gray-500" dir="ltr">
              +{CONTACT_WHATSAPP}
            </span>
          </a>
          <a
            href={CONTACT_FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-[7rem] flex-col items-center justify-center gap-2 rounded-2xl border border-green-100 bg-white p-5 shadow-sm transition hover:border-green-300 hover:shadow-md"
          >
            <Facebook className="h-7 w-7 text-blue-600" />
            <span className="font-semibold text-green-900">فيسبوك</span>
            <span className="text-xs text-gray-500">صفحة الأستاذ سعيد الفقي</span>
          </a>
          <a
            href={CONTACT_YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-[7rem] flex-col items-center justify-center gap-2 rounded-2xl border border-green-100 bg-white p-5 shadow-sm transition hover:border-green-300 hover:shadow-md min-[480px]:col-span-2 lg:col-span-1"
          >
            <Youtube className="h-7 w-7 text-red-600" />
            <span className="font-semibold text-green-900">يوتيوب</span>
            <span className="text-xs text-gray-500">شروحات ودروس مجانية</span>
          </a>
        </section>

        <section className="mb-10 rounded-2xl border border-green-100 bg-white p-5 sm:p-6">
          <div className="mb-4 flex items-center gap-2">
            <Mail className="h-5 w-5 text-green-700" />
            <h2 className="text-lg font-bold text-green-900 sm:text-xl">البريد والدعم</h2>
          </div>
          <p className="mb-2 text-sm text-gray-600 sm:text-base">
            راسلنا على{' '}
            <a className="font-semibold text-green-800 underline" href="mailto:info@nahgelazhar.com">
              info@nahgelazhar.com
            </a>
          </p>
          <p className="flex items-start gap-2 text-sm text-gray-600 sm:text-base">
            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-green-700" />
            للدعم الفني السريع استخدم واتساب خلال ساعات العمل الرسمية.
          </p>
        </section>

        <section>
          <div className="mb-4 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-gold-600" />
            <h2 className="text-lg font-bold text-green-900 sm:text-xl">أسئلة شائعة</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((item) => (
              <details
                key={item.q}
                className="group rounded-xl border border-green-100 bg-white p-4 open:shadow-sm"
              >
                <summary className="cursor-pointer list-none text-sm font-semibold text-green-900 sm:text-base">
                  {item.q}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{item.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <Link to="/refund-policy" className="text-green-800 underline hover:text-gold-600">
              سياسة الاسترجاع
            </Link>
            <Link to="/privacy" className="text-green-800 underline hover:text-gold-600">
              سياسة الخصوصية
            </Link>
            <Link to="/terms" className="text-green-800 underline hover:text-gold-600">
              الشروط والأحكام
            </Link>
            <Link to="/parent-monitoring" className="text-green-800 underline hover:text-gold-600">
              متابعة ولي الأمر
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactPage;
